import { type SuiValidatorSummary } from '@mysten/sui.js/client';
import { Text } from '@mysten/ui';

import type { StakePositionView } from '~/hooks/useCpcStakePositions';
import { useT } from '~/i18n/LocaleContext';
import { ImageIcon } from '~/ui/ImageIcon';
import { ValidatorLink } from '~/ui/InternalLink';
import { CPC_SYMBOL, CPC_STAKE_LOCK_EPOCHS } from '~/utils/cpcCoin';
import { displayValidatorHeading } from '~/utils/displayValidator';
import { shortenAddress } from '~/utils/explorerFormat';

type Props = {
	positions: StakePositionView[];
	validatorByAddress: Map<string, SuiValidatorSummary>;
	isBusy: boolean;
	onClaim: (pos: StakePositionView) => void;
	onRedeem: (pos: StakePositionView) => void;
};

function LockProgressBar({ percent }: { percent: number }) {
	const t = useT();
	const pct = Math.min(100, Math.max(0, Math.round(percent)));
	return (
		<div className="mt-2">
			<div className="flex justify-between text-[10px] text-[#94a3b8] mb-1">
				<span>{t('stake.lockProgress')}</span>
				<span>{pct}%</span>
			</div>
			<div className="h-1.5 rounded-full bg-[#1E293B] overflow-hidden">
				<div
					className="h-full bg-amber-400/80 rounded-full"
					style={{ width: `${pct}%` }}
				/>
			</div>
		</div>
	);
}

function formatClaimableReward(pos: StakePositionView): { text: string; hasReward: boolean } {
	const raw = pos.estimatedRewardCpc?.trim();
	if (raw && raw !== '—') {
		const n = Number(raw);
		if (Number.isFinite(n)) {
			return { text: `${raw} ${CPC_SYMBOL}`, hasReward: n > 0 };
		}
	}
	return { text: `0 ${CPC_SYMBOL}`, hasReward: false };
}

export function CpcStakePositionsList({
	positions,
	validatorByAddress,
	isBusy,
	onClaim,
	onRedeem,
}: Props) {
	const t = useT();
	if (!positions.length) {
		return (
			<Text variant="body/medium" color="dark">
				{t('stake.noDelegations')}
			</Text>
		);
	}

	return (
		<div className="space-y-3">
			{positions.map((pos) => {
				const meta = validatorByAddress.get(pos.validatorAddress.toLowerCase());
				const heading = meta
					? displayValidatorHeading(meta.name, meta.description)
					: '';
				const key =
					pos.stakedSuiId ?? pos.fungibleStakedSuiId ?? `${pos.validatorAddress}-${pos.kind}`;
				const claimable = formatClaimableReward(pos);

				return (
					<div
						key={key}
						className="rounded-xl border border-[#1E293B] bg-[#0F172A]/50 p-4"
					>
						<div className="flex flex-wrap items-start justify-between gap-3">
							<div className="flex items-center gap-3 min-w-0">
								{meta ? (
									<ImageIcon
										src={meta.imageUrl}
										size="sm"
										fallback={heading}
										label={heading}
										circle
									/>
								) : null}
								<div className="min-w-0">
									{meta ? (
										<ValidatorLink
											address={pos.validatorAddress}
											label={heading}
											className="font-mono text-sm font-semibold text-[#38BDF8] hover:text-sky-300 break-all"
										/>
									) : (
										<span className="font-mono text-sm text-white">
											{shortenAddress(pos.validatorAddress)}
										</span>
									)}
									<div className="text-xs text-[#94a3b8] mt-0.5 capitalize">
										{pos.kind === 'fungible_staked_sui' ? t('stake.fungibleCert') : 'StakedSui'} ·{' '}
										{pos.status}
									</div>
								</div>
							</div>
							<div className="text-right">
								<div className="text-lg font-bold text-white tabular-nums">
									{pos.principalCpc} {CPC_SYMBOL}
								</div>
								<div
									className={`text-xs mt-0.5 tabular-nums ${
										claimable.hasReward ? 'text-[#4ade80] font-semibold' : 'text-[#94a3b8]'
									}`}
								>
									{t('stake.claimable')} {claimable.text}
								</div>
							</div>
						</div>

						<div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs text-[#aeb9d0]">
							<div>
								<div className="text-[#94a3b8]">{t('stake.activeEpoch')}</div>
								<div className="text-white tabular-nums">{pos.stakeActiveEpoch}</div>
							</div>
							<div>
								<div className="text-[#94a3b8]">{t('stake.unlockEpoch')}</div>
								<div className="text-white tabular-nums">{pos.lockUntilEpoch}</div>
							</div>
							<div>
								<div className="text-[#94a3b8]">{t('stake.epochsLeft')}</div>
								<div className="text-white tabular-nums">
									{pos.principalLocked ? pos.epochsUntilPrincipalUnlock : 0}
								</div>
							</div>
							<div>
								<div className="text-[#94a3b8]">{t('stake.lockPeriod')}</div>
								<div className="text-white">{CPC_STAKE_LOCK_EPOCHS} epochs</div>
							</div>
							<div>
								<div className="text-[#94a3b8]">{t('stake.claimableRewards')}</div>
								<div
									className={`tabular-nums font-medium ${
										claimable.hasReward ? 'text-[#4ade80]' : 'text-white'
									}`}
								>
									{claimable.text}
								</div>
							</div>
						</div>

						{pos.principalLocked ? (
							<LockProgressBar percent={pos.principalLockProgressPercent} />
						) : (
							<div className="mt-2 text-xs text-emerald-400">{t('stake.principalUnlocked')}</div>
						)}

						<div className="mt-3 flex flex-wrap gap-2">
							{pos.canClaimRewards ? (
								<button
									type="button"
									disabled={isBusy}
									onClick={() => onClaim(pos)}
									className="rounded-lg border border-emerald-400/50 bg-emerald-600 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-emerald-500 disabled:opacity-50"
								>
									{t('stake.claimRewards')}
								</button>
							) : null}
							{pos.canRedeemPrincipal ? (
								<button
									type="button"
									disabled={isBusy}
									onClick={() => onRedeem(pos)}
									className="rounded-lg border border-amber-400/50 bg-amber-600 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-amber-500 disabled:opacity-50"
								>
									{t('stake.redeemPrincipal')}
								</button>
							) : pos.principalLocked ? (
								<span className="text-xs text-[#94a3b8] self-center">
									{t('stake.redeemAfterUnlock').replace('{epoch}', String(pos.lockUntilEpoch))}
								</span>
							) : null}
						</div>
					</div>
				);
			})}
		</div>
	);
}
